/** Tracks DOM/Phaser overlays that freeze local player movement (world keeps simulating). */
const openOverlays = new Set<string>();

let onControlsRestored: (() => void) | null = null;

/** Register listener fired when the last overlay gate closes. */
export function setPlayerControlsRestoredListener(fn: (() => void) | null): void {
  onControlsRestored = fn;
}

export function getOpenOverlayIds(): string[] {
  return [...openOverlays];
}

function maybeNotifyControlsRestored(wasFrozen: boolean): void {
  if (wasFrozen && openOverlays.size === 0) onControlsRestored?.();
}

export function setPlayerOverlayOpen(id: string, open: boolean): void {
  if (!id) return;
  const wasFrozen = openOverlays.size > 0;
  if (open) openOverlays.add(id);
  else openOverlays.delete(id);
  maybeNotifyControlsRestored(wasFrozen);
}

export function isPlayerInputFrozen(): boolean {
  return openOverlays.size > 0;
}

/** Case notes or interview Phaser panels (not radio/journal). */
export function isNpcConversationOverlayOpen(): boolean {
  return openOverlays.has("npc-case-notes") || openOverlays.has("npc-dialog");
}

/** True when `el` is a DOM control where the user types or selects text. */
export function isDomTypingElement(el: EventTarget | null | undefined): boolean {
  if (!el || typeof el !== "object") return false;
  const node = el as { tagName?: unknown; isContentEditable?: boolean };
  if (typeof node.tagName !== "string") return false;
  const tag = node.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (node.isContentEditable === true) return true;
  return false;
}

export function isTextInputFocused(): boolean {
  if (typeof document === "undefined") return false;
  return isDomTypingElement(document.activeElement);
}

/** True when Phaser should read movement keys (WASD / arrows). */
export function shouldPhaserReadMovementKeys(): boolean {
  if (isPlayerInputFrozen()) return false;
  if (isTextInputFocused()) return false;
  return true;
}

/** Clears all overlay gates (e.g. session end). */
export function resetPlayerInputOverlays(): void {
  const wasFrozen = openOverlays.size > 0;
  openOverlays.clear();
  maybeNotifyControlsRestored(wasFrozen);
}

/** Blur focused controls inside `root` so movement keys work again. */
export function releaseDomOverlayFocus(root: HTMLElement | null | undefined): void {
  if (!root || typeof document === "undefined") return;
  const active = document.activeElement as { blur?: () => void } | null;
  const container = root as { contains?: (node: unknown) => boolean };
  if (active?.blur && container.contains?.(active)) active.blur();
}

/** Return focus to the Phaser canvas when possible. */
export function focusPlayfieldCanvas(): void {
  if (typeof document === "undefined") return;
  const canvas = document.querySelector("#game-host canvas") as { focus?: () => void } | null;
  canvas?.focus?.();
}
